/**
 * ===========================================================================
 *  MISSION OVERLAY — objective plaque + capture meter
 * ===========================================================================
 *
 * The HUD (src/ui/hud.js) owns health, KO, minimap, tide, banners and the
 * mission log. It has no objective ticker and no capture gauge, and it is not
 * this module's file to edit — so the mission layer mounts its own two
 * elements INSIDE the published `world.ui.root` and styles them with the same
 * grammar (`--u` scale unit, gold rules, letter-spaced uppercase labels, cut
 * corners). They inherit `--u`, so they scale with the rest of the HUD, and
 * `.sb.off{display:none}` hides them with it.
 *
 * The same rules as the HUD apply: no @keyframes (Playwright screenshots with
 * animations disabled), no external fonts, no per-frame allocation — every
 * DOM write goes through a last-value cache, so a still frame writes nothing.
 */

const CSS = `
/* top/height are deliberately tight: the HUD's own mission log is anchored at
   top:112u and this plaque must never grow into its newest line. */
.sb .mob{position:absolute; left:calc(var(--u)*30); top:calc(var(--u)*20);
  width:calc(var(--u)*300); opacity:0}
.sb .mob .plq{
  padding:calc(var(--u)*6) calc(var(--u)*13) calc(var(--u)*7);
  background:linear-gradient(180deg, rgba(14,10,8,.70), rgba(6,5,4,.44));
  clip-path:polygon(0 0, 100% 0, 100% calc(100% - var(--u)*12),
                    calc(100% - var(--u)*12) 100%, 0 100%);
  box-shadow:inset calc(var(--u)*2) 0 0 rgba(228,190,116,.55),
             0 calc(var(--u)*3) calc(var(--u)*12) rgba(0,0,0,.55)}
.sb .mob .ohead{display:flex; align-items:center; gap:calc(var(--u)*7)}
.sb .mob .ohead i{width:calc(var(--u)*6); height:calc(var(--u)*6); display:block;
  transform:rotate(45deg); background:var(--gold);
  box-shadow:0 0 calc(var(--u)*9) rgba(230,180,90,.75)}
.sb .mob .okey{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*4);
  text-transform:uppercase; color:rgba(232,196,140,.72)}
.sb .mob .ophase{margin-left:auto; font-size:calc(var(--u)*8); font-weight:700;
  letter-spacing:calc(var(--u)*2.6); text-transform:uppercase; color:rgba(226,190,124,.42)}
.sb .mob .otxt{font-size:calc(var(--u)*17); letter-spacing:calc(var(--u)*1.6);
  line-height:1.18; color:#fff2d8; margin-top:calc(var(--u)*4)}
.sb .mob .osub{font-size:calc(var(--u)*9.5); font-weight:700; letter-spacing:calc(var(--u)*1.8);
  color:rgba(226,190,124,.62); margin-top:calc(var(--u)*3)}
.sb .mob .osec{margin-top:calc(var(--u)*5); border-top:calc(var(--u)*1) solid rgba(228,190,116,.18);
  padding-top:calc(var(--u)*4)}
.sb .mob .osec div{display:flex; align-items:flex-start; gap:calc(var(--u)*6);
  font-size:calc(var(--u)*10.5); letter-spacing:calc(var(--u)*.8); line-height:1.25;
  color:rgba(238,226,200,.80); margin-bottom:0}
.sb .mob .osec div::before{content:""; flex:0 0 auto; width:calc(var(--u)*4);
  height:calc(var(--u)*4); margin-top:calc(var(--u)*4); transform:rotate(45deg);
  background:rgba(228,190,116,.55)}
.sb .mob .osec div.warn{color:#ffcf9a}
.sb .mob .osec div.good{color:#c6e9a8}
.sb .mob .osec div.bad{color:#ff9c86}

.sb .mcap{position:absolute; left:50%; bottom:calc(var(--u)*46);
  width:calc(var(--u)*286); margin-left:calc(var(--u)*-143); opacity:0; text-align:center}
.sb .mcap .crow{display:flex; align-items:baseline; justify-content:space-between;
  margin-bottom:calc(var(--u)*5)}
.sb .mcap .cname{font-size:calc(var(--u)*11); letter-spacing:calc(var(--u)*2.6);
  text-transform:uppercase; font-weight:700; color:#f6e6c4}
.sb .mcap .cpct{font-size:calc(var(--u)*11); font-weight:700; letter-spacing:calc(var(--u)*1.4);
  color:rgba(240,206,144,.9); font-variant-numeric:tabular-nums}
.sb .mcap .cbar{position:relative; height:calc(var(--u)*9); transform:skewX(-15deg);
  overflow:hidden; background:linear-gradient(180deg, rgba(4,3,2,.94), rgba(20,14,10,.9));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(0,0,0,.9),
             0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.55)}
.sb .mcap .cbar>i{position:absolute; left:0; top:0; bottom:0; width:100%;
  transform-origin:0 50%; display:block;
  background:linear-gradient(180deg,#9ed0ff 0%,#4f86dd 42%,#1e4796 100%)}
.sb .mcap.foe .cbar>i{background:linear-gradient(180deg,#ffb59a 0%,#ff6a4a 34%,#8c1c10 100%)}
.sb .mcap .cbar>b{position:absolute; top:0; bottom:0; width:calc(var(--u)*2); display:block;
  background:rgba(255,240,208,.9); box-shadow:0 0 calc(var(--u)*8) rgba(255,220,160,.8)}
.sb .mcap .ctag{margin-top:calc(var(--u)*4); font-size:calc(var(--u)*8.5); font-weight:700;
  letter-spacing:calc(var(--u)*4.2); text-transform:uppercase; color:rgba(232,196,140,.66)}
.sb .mcap.foe .ctag{color:rgba(255,150,120,.78)}

/* --------------------------------------------------------------------------
 * WORKAROUND — results modal width. See docs/REQUESTS.md (mission -> ui).
 * src/ui/style.js:308 is ".sb .res{width:calc(var(--u)*620)}", but .res is the
 * MODAL (div class="modal res"), not the card inside it: an absolutely
 * positioned inset:0 box with an explicit width stops honouring right:0, so
 * the results screen and its scrim only cover the left 620u of the frame and
 * the battle keeps playing, undimmed, across the rest of it. The mission is
 * what puts that screen on screen, so it carries the patch until hud.js moves
 * the width onto ".res .body". A straight revert of that one rule, nothing
 * else.
 * ------------------------------------------------------------------------ */
.sb .modal.res{width:auto; left:0; right:0}
.sb .res>.body{width:calc(var(--u)*620); max-width:100%}
`;

/** Cached DOM writes — a still frame touches nothing. */
function tx(el, s) { if (el.__t === s) return; el.__t = s; el.textContent = s; }
function op(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__o !== undefined && Math.abs(el.__o - v) < 0.004) return;
  el.__o = v; el.style.opacity = v.toFixed(3);
}
function sx(el, v) {
  v = v < 0 ? 0 : v > 1 ? 1 : v;
  if (el.__s !== undefined && Math.abs(el.__s - v) < 0.0015) return;
  el.__s = v; el.style.transform = 'scaleX(' + v.toFixed(4) + ')';
}

export function createMissionHud() {
  let mounted = false;
  let root = null, styleEl = null, mob = null, cap = null;
  const R = {};
  const S = { show: 0, capShow: 0, capV: 0, pct: -1, foe: false };
  const secs = [];

  function mount(w) {
    if (mounted) return true;
    const host = w.ui && w.ui.root;
    if (!host || !host.appendChild) return false;

    styleEl = document.createElement('style');
    styleEl.textContent = CSS;
    host.appendChild(styleEl);

    mob = document.createElement('div');
    mob.className = 'mob';
    mob.innerHTML = `<div class="plq">
        <div class="ohead"><i></i><span class="okey">Objective</span><span class="ophase"></span></div>
        <div class="otxt ser"></div>
        <div class="osub"></div>
        <div class="osec"></div>
      </div>`;
    host.appendChild(mob);

    cap = document.createElement('div');
    cap.className = 'mcap';
    cap.innerHTML = `<div class="crow"><span class="cname"></span><span class="cpct"></span></div>
      <div class="cbar"><i></i><b></b></div>
      <div class="ctag">Capturing</div>`;
    host.appendChild(cap);

    R.txt = mob.querySelector('.otxt');
    R.sub = mob.querySelector('.osub');
    R.phase = mob.querySelector('.ophase');
    R.sec = mob.querySelector('.osec');
    R.cname = cap.querySelector('.cname');
    R.cpct = cap.querySelector('.cpct');
    R.cfill = cap.querySelector('.cbar>i');
    R.cedge = cap.querySelector('.cbar>b');
    R.ctag = cap.querySelector('.ctag');
    for (let i = 0; i < 3; i++) {
      const d = document.createElement('div');
      d.style.display = 'none';
      R.sec.appendChild(d);
      secs.push({ el: d, txt: '', kind: '' });
    }
    mounted = true;
    return true;
  }

  /**
   * `obj` is the primary objective record (or null), `list` the secondary
   * ones. Called only when something actually changes.
   */
  function setObjective(obj, list, phaseName) {
    if (!mounted) return;
    tx(R.txt, obj ? obj.text : '');
    tx(R.sub, obj && obj.sub ? obj.sub : '');
    tx(R.phase, phaseName || '');
    const n = list ? list.length : 0;
    for (let i = 0; i < secs.length; i++) {
      const s = secs[i], o = i < n ? list[i] : null;
      const t = o ? o.text : '';
      if (s.txt !== t) {
        s.txt = t;
        s.el.textContent = t;
        s.el.style.display = t ? 'flex' : 'none';
      }
      const k = o ? (o.state === 'done' ? 'good' : o.state === 'failed' ? 'bad' : o.kind === 'defend' || o.kind === 'protect' ? 'warn' : '') : '';
      if (s.kind !== k) { s.kind = k; s.el.className = k; }
    }
  }

  /** Per rendered frame. `capRec` is the live capture record or null. */
  function frame(w, realDt, visible, capRec) {
    if (!mounted) return;
    const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
    S.show += ((visible ? 1 : 0) - S.show) * Math.min(1, k * 6);
    op(mob, S.show);

    const live = !!capRec;
    S.capShow += ((live ? 1 : 0) - S.capShow) * Math.min(1, k * (live ? 9 : 4));
    op(cap, S.capShow);
    if (S.capShow < 0.004) return;
    if (live) {
      S.capV += (capRec.progress - S.capV) * Math.min(1, k * 12);
      const foe = capRec.capTeam === 1;
      if (foe !== S.foe) { S.foe = foe; cap.classList.toggle('foe', foe); }
      tx(R.cname, capRec.name || 'Position');
      const p = Math.round(S.capV * 100);
      if (p !== S.pct) { S.pct = p; R.cpct.textContent = p + '%'; }
      tx(R.ctag, capRec.contested ? 'Contested' : foe ? 'Losing Ground' : 'Capturing');
    }
    sx(R.cfill, S.capV);
    // percentage, not pixels: reading clientWidth every frame would force a
    // layout flush on a HUD that is otherwise write-only.
    const ex = S.capV * 100;
    if (R.cedge.__x === undefined || Math.abs(R.cedge.__x - ex) > 0.12) {
      R.cedge.__x = ex;
      R.cedge.style.left = ex.toFixed(2) + '%';
    }
  }

  function clear() {
    if (!mounted) return;
    S.capV = 0; S.pct = -1;
    setObjective(null, null, '');
  }

  function dispose() {
    mob && mob.remove(); cap && cap.remove(); styleEl && styleEl.remove();
    mounted = false; secs.length = 0;
  }

  return { mount, setObjective, frame, clear, dispose, get mounted() { return mounted; },
    get el() { return mob; } };
}
